// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { isEmpty, intersection } from 'lodash/fp';

// Utils
import { hasCommandModifier, hasShiftKey } from '../../utils/keyboard/keyboardUtility';

// Selectors
import { getCurrentFocus } from '../../reducers/focusSelector';
import { getSelectedElementIds } from '../../element/selection/selectedElementsSelector';
import { getCurrentlyEditingId } from '../../element/selection/currentlyEditingSelector';

// Actions
import {
    deselectAllElements,
    finishEditingElement,
    removeSelectedElements,
} from '../../element/selection/selectionActions';
import { setFocusSection } from '../../reducers/focusActions';

// Constants
import { WORKSPACE_SECTIONS } from '../../workspace/workspaceConstants';

const mapStateToProps = createStructuredSelector({
    currentFocus: getCurrentFocus,
    selectedElementIds: getSelectedElementIds,
    currentlyEditingId: getCurrentlyEditingId,
});

const mapDispatchToProps = (dispatch) => ({
    // Need to call action to get currentFocus because react-contextmenu renders
    // before the props update therefore getting the old current focus if mapStateToProps
    dispatchGetCurrentFocus: () => dispatch((_, getState) => getCurrentFocus(getState())),
    dispatchDeselectAllElements: () => dispatch(deselectAllElements()),
    dispatchSetFocusSection: (section) => dispatch(setFocusSection(section)),
    dispatchDeselectElements: ({ ids }) => dispatch(removeSelectedElements({ ids })),
    dispatchFinishEditingElement: (id) => dispatch(finishEditingElement(id)),
});

@connect(mapStateToProps, mapDispatchToProps)
class PopupBoardSectionFocusManager extends React.Component {
    componentWillMount() {
        const { dispatchSetFocusSection, section } = this.props;
        dispatchSetFocusSection(section);
    }

    componentWillUnmount() {
        const {
            section,
            childElementIds = [],
            dispatchSetFocusSection,
            selectedElementIds,
            currentlyEditingId,
            dispatchDeselectElements,
            dispatchFinishEditingElement,
            dispatchGetCurrentFocus,
        } = this.props;

        if (!isEmpty(selectedElementIds)) {
            const popupSelectedElementIds = intersection(selectedElementIds.toArray(), childElementIds);

            if (!isEmpty(popupSelectedElementIds)) {
                dispatchDeselectElements({ ids: popupSelectedElementIds });
            }

            if (currentlyEditingId) dispatchFinishEditingElement(currentlyEditingId);
        }

        const currentFocus = dispatchGetCurrentFocus();

        // Already set focus elsewhere
        if (currentFocus !== section) return;

        dispatchSetFocusSection(WORKSPACE_SECTIONS.CANVAS);
    }

    onMouseDown = (event) => {
        const { section, dispatchSetFocusSection, dispatchDeselectAllElements, ignoredClicksSelector } = this.props;

        // If the target is a tooltip, ignore the click
        const ignoredClickAncestor = event.target.closest(ignoredClicksSelector);

        if (ignoredClickAncestor) return;

        dispatchSetFocusSection(section);

        if (hasCommandModifier(event)) return;
        if (hasShiftKey(event)) return;

        // If the click occurs within an Element, then don't deselect all
        if (event.target.closest('.Element')) return;

        dispatchDeselectAllElements();
    };

    render() {
        const { children, divRef } = this.props;
        return (
            <div className="PopupBoardSectionFocusManager" onMouseDown={this.onMouseDown} ref={divRef}>
                {children}
            </div>
        );
    }
}

PopupBoardSectionFocusManager.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,

    section: PropTypes.string.isRequired,
    childElementIds: PropTypes.array,
    ignoredClicksSelector: PropTypes.string,

    dispatchDeselectAllElements: PropTypes.func,
    dispatchDeselectElements: PropTypes.func,
    dispatchFinishEditingElement: PropTypes.func,
    dispatchSetFocusSection: PropTypes.func,
    dispatchGetCurrentFocus: PropTypes.func,

    currentFocus: PropTypes.string,
    selectedElementIds: PropTypes.object,
    currentlyEditingId: PropTypes.string,

    divRef: PropTypes.func,
};

export default PopupBoardSectionFocusManager;
